'use client';

import { Table } from '@tanstack/react-table';
import { XIcon } from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
  facetedFilters?: React.ReactNode;
  search?: DataTableSearchProps;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  children?: React.ReactNode;
  onClearFilters?: () => void;
  hasCustomFilters?: boolean;
}

interface DataTableSearchProps {
  placeholder: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

/**
 * @example
 * <DataTableToolbar table={table} filters={[
 *    <DataTableFacetedFilter
 *        column={table.getColumn('status')}
 *        title="Status"
 *        options={statuses}
 *    />,
 *    <DataTableFacetedFilter
 *        column={table.getColumn('priority')}
 *        title="Priority"
 *        options={priorities}
 *    />,
 * ]} />
 */
export function DataTableToolbar<TData>({
  table,
  facetedFilters,
  search,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  children,
  onClearFilters,
  hasCustomFilters,
}: DataTableToolbarProps<TData>) {
  const hasColumnFilters = table.getState().columnFilters.length > 0;
  const hasGlobalFilter = table.getState().globalFilter;
  const isFiltered = hasColumnFilters || hasGlobalFilter || hasCustomFilters;

  const handleClearFilters = () => {
    table.resetColumnFilters();
    table.setGlobalFilter('');
    onClearFilters?.();
  };

  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-1 items-center gap-2 flex-wrap">
        {(search || searchPlaceholder) && (
          <Input
            placeholder={
              search?.placeholder || searchPlaceholder || 'Search...'
            }
            value={search?.value || searchValue || ''}
            onChange={
              search?.onChange || ((e) => onSearchChange?.(e.target.value))
            }
            className="h-8 w-[150px] lg:w-[250px]"
          />
        )}
        {facetedFilters}
        {children}
        {isFiltered && (
          <Button variant="ghost" size="sm" onClick={handleClearFilters}>
            Clear filters
            <XIcon />
          </Button>
        )}
      </div>
      <div className="flex items-center gap-2">
        {/* <DataTableViewOptions table={table} /> */}
        {/* <Button size="sm">Add Task</Button> */}
      </div>
    </div>
  );
}
